import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchLabelPrintStatus,
  markLabelRangePrinted,
  prepareLabelRange,
} from '../api';

function boxUrl(boxNummer) {
  return `${window.location.origin}/box/${encodeURIComponent(boxNummer)}`;
}

function displayBoxNumber(sequence) {
  return `B${String(sequence).padStart(3, '0')}`;
}

export default function LabelPrint({ onClose }) {
  const [von, setVon] = useState(5);
  const [bis, setBis] = useState(8);
  const [status, setStatus] = useState(null);
  const [boxesToPrint, setBoxesToPrint] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printDone, setPrintDone] = useState(false);
  const [error, setError] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  useEffect(() => {
    let active = true;
    fetchLabelPrintStatus()
      .then(data => {
        if (!active) return;
        setStatus(data);
        setVon(data.naechster_bereich.von);
        setBis(data.naechster_bereich.bis);
      })
      .catch(err => {
        if (active) setError('Druckstand konnte nicht geladen werden: ' + err.message);
      });
    return () => { active = false; };
  }, []);

  function clearPreparedRange() {
    setBoxesToPrint([]);
    setDuplicateWarning(null);
    setPrintDone(false);
    setError('');
  }

  function handleFromChange(event) {
    const value = event.target.value;
    setVon(value);
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed)) setBis(parsed + 3);
    clearPreparedRange();
  }

  function handleToChange(event) {
    setBis(event.target.value);
    clearPreparedRange();
  }

  function parsedRange() {
    const from = Number.parseInt(von, 10);
    const to = Number.parseInt(bis, 10);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1) return null;
    if (to - from !== 3) return null;
    return { from, to };
  }

  async function prepare(forceDuplicate = false) {
    const range = parsedRange();
    if (!range) {
      setError('Ein Bogen muss genau vier Boxen enthalten, zum Beispiel 5 bis 8.');
      return;
    }
    setLoading(true);
    setError('');
    setDuplicateWarning(null);
    try {
      const data = await prepareLabelRange(range.from, range.to, forceDuplicate);
      setBoxesToPrint(data.boxen);
      setPrintDone(false);
    } catch (err) {
      if (err.code === 'ETIKETTEN_BEREITS_GEDRUCKT') {
        setDuplicateWarning(err.details.gedruckte_boxen || []);
      } else {
        setError('Etiketten konnten nicht vorbereitet werden: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  function handlePrepare(event) {
    event.preventDefault();
    prepare(false);
  }

  async function handlePrint() {
    const range = parsedRange();
    if (!range || boxesToPrint.length !== 4) return;
    setPrinting(true);
    setError('');
    try {
      const newStatus = await markLabelRangePrinted(range.from, range.to);
      setStatus(newStatus);
      setPrintDone(true);
      window.print();
    } catch (err) {
      setError('Der Druckstand konnte nicht gespeichert werden: ' + err.message);
    } finally {
      setPrinting(false);
    }
  }

  const labelItems = boxesToPrint.flatMap(box => [
    { box, copy: 1 },
    { box, copy: 2 },
  ]);
  const firstBox = boxesToPrint[0];
  const range = parsedRange();

  return (
    <div className="label-print-overlay" style={styles.overlay}>
      <div className="label-print-modal" style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>A4-Etiketten drucken</h2>
            <div style={styles.subtitle}>Vier Boxen pro Bogen · zwei Etiketten je Box</div>
          </div>
          <button type="button" style={styles.close} onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div style={styles.statusCard}>
          <div>
            <div style={styles.statusLabel}>Gespeicherter Druckstand</div>
            <strong style={styles.statusValue}>
              {status?.gedruckt_bis ? `Gedruckt bis ${status.gedruckt_bis}` : 'Noch keine Etiketten gespeichert'}
            </strong>
          </div>
          {status?.naechster_bereich && (
            <div style={styles.nextRange}>
              Nächster Bogen: {displayBoxNumber(status.naechster_bereich.von)}–{displayBoxNumber(status.naechster_bereich.bis)}
            </div>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form style={styles.rangeRow} onSubmit={handlePrepare}>
          <label style={styles.label}>
            Von Box
            <input
              style={styles.input}
              type="number"
              min="1"
              value={von}
              onChange={handleFromChange}
            />
          </label>
          <label style={styles.label}>
            Bis Box
            <input
              style={styles.input}
              type="number"
              min="4"
              value={bis}
              onChange={handleToChange}
            />
          </label>
          <button type="submit" style={styles.primaryButton} disabled={loading}>
            {loading ? 'Prüft…' : 'Druckbogen vorbereiten'}
          </button>
        </form>

        <div style={styles.note}>
          Beispiel: 5 bis 8 ergibt einen vollständigen A4-Bogen mit acht Etiketten. Eine Box wird erst beim Erfassen oder Scannen angelegt - das Drucken allein erhöht den Bestand nicht.
        </div>

        {duplicateWarning && (
          <div style={styles.warning}>
            <div>
              <strong>Dieser Bereich wurde schon gedruckt.</strong>
              <div style={styles.warningText}>
                Bereits gespeichert: {duplicateWarning.join(', ')}. Möchtest du diese Etiketten wirklich erneut drucken?
              </div>
            </div>
            <div style={styles.warningActions}>
              <button type="button" style={styles.cancelButton} onClick={() => setDuplicateWarning(null)}>
                Abbrechen
              </button>
              <button type="button" style={styles.warningButton} onClick={() => prepare(true)} disabled={loading}>
                Ja, erneut vorbereiten
              </button>
            </div>
          </div>
        )}

        {boxesToPrint.length === 4 && range && (
          <>
            <div style={styles.selectionHeader}>
              <div>
                <strong>{displayBoxNumber(range.from)} bis {displayBoxNumber(range.to)}</strong>
                <div style={styles.small}>8 Etiketten auf einem A4-Bogen</div>
              </div>
              <button
                type="button"
                style={printDone ? styles.doneButton : styles.printButton}
                onClick={handlePrint}
                disabled={printing || printDone}
              >
                {printing ? 'Speichert…' : printDone ? 'Druck gespeichert' : 'Drucken & merken'}
              </button>
            </div>

            {firstBox && (
              <div style={styles.previewWrap}>
                <div style={styles.previewLabel}>
                  <QRCodeSVG value={boxUrl(firstBox.box_nummer)} size={92} level="M" marginSize={1} />
                  <div style={styles.previewNumber}>{firstBox.box_nummer}</div>
                </div>
                <div style={styles.printHint}>
                  Im Druckdialog: Papierformat A4, Skalierung 100 %, Ränder „Keine“ und Kopf-/Fußzeilen aus. Der Bereich wird beim Öffnen des Druckdialogs als gedruckt gespeichert.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="label-print-pages">
        {labelItems.length > 0 && (
          <div className="label-print-sheet">
            {labelItems.map(({ box, copy }) => (
              <div className="label-print-label" key={`${box.id || box.box_nummer}-${copy}`}>
                <QRCodeSVG
                  className="label-print-qr"
                  value={boxUrl(box.box_nummer)}
                  level="M"
                  marginSize={1}
                />
                <div className="label-print-number">{box.box_nummer}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(23,32,51,0.62)', backdropFilter: 'blur(8px)', padding: 16, overflowY: 'auto' },
  modal: { width: '100%', maxWidth: 660, margin: '16px auto 32px', border: '1px solid rgba(221,226,235,0.9)', borderRadius: 20, padding: 26, background: '#fff', boxShadow: '0 24px 70px rgba(25,35,62,0.22)' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 },
  title: { margin: 0, color: '#172033', fontSize: 25, letterSpacing: '-0.035em' },
  subtitle: { color: '#697386', fontSize: 13, marginTop: 5 },
  close: { border: 'none', borderRadius: 12, width: 40, height: 40, background: '#f0f2f6', color: '#657083', fontSize: 25, lineHeight: 1 },
  statusCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: 14, marginBottom: 16, border: '1px solid #c5e7dc', borderRadius: 14, background: '#eef8f4', color: '#11614f' },
  statusLabel: { fontSize: 12, marginBottom: 3, color: '#47766a' },
  statusValue: { fontSize: 18 },
  nextRange: { padding: '8px 11px', borderRadius: 9, background: '#fff', color: '#11614f', fontSize: 13, fontWeight: 750 },
  rangeRow: { display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 5, color: '#374151', fontSize: 13, fontWeight: 700 },
  input: { width: 105, padding: '11px 12px', border: '1px solid #d9dee8', borderRadius: 11, fontSize: 16 },
  primaryButton: { border: 'none', borderRadius: 11, padding: '12px 17px', background: '#3346c8', color: '#fff', fontSize: 14, fontWeight: 800, boxShadow: '0 7px 18px rgba(51,70,200,0.20)' },
  note: { padding: 12, borderRadius: 11, background: '#f4f5f8', color: '#697386', fontSize: 13, lineHeight: 1.5 },
  error: { padding: 10, borderRadius: 8, marginBottom: 12, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: 14 },
  warning: { marginTop: 14, padding: 14, borderRadius: 12, background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa' },
  warningText: { marginTop: 5, fontSize: 13, lineHeight: 1.45 },
  warningActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  cancelButton: { border: '1px solid #fdba74', borderRadius: 9, padding: '9px 12px', background: '#fff', color: '#9a3412', fontWeight: 750 },
  warningButton: { border: 'none', borderRadius: 9, padding: '9px 12px', background: '#c2410c', color: '#fff', fontWeight: 800 },
  selectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 18, paddingTop: 16, borderTop: '1px solid #e2e8f0' },
  printButton: { border: 'none', borderRadius: 11, padding: '12px 20px', background: '#137a62', color: '#fff', fontSize: 14, fontWeight: 800 },
  doneButton: { border: 'none', borderRadius: 11, padding: '12px 20px', background: '#d8e5e1', color: '#37665a', fontSize: 14, fontWeight: 800 },
  small: { fontSize: 12, color: '#64748b', marginTop: 3, fontWeight: 400 },
  previewWrap: { marginTop: 14, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  previewLabel: { width: 260, minHeight: 148, border: '1px solid #d9dee8', borderRadius: 13, padding: 14, display: 'flex', alignItems: 'center', gap: 18, background: '#fff', boxShadow: '0 4px 16px rgba(23,32,51,0.06)' },
  previewNumber: { fontSize: 32, fontWeight: 900, letterSpacing: 1, color: '#000' },
  printHint: { flex: 1, minWidth: 200, color: '#475569', fontSize: 13, lineHeight: 1.5 },
};
